/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/activity/ActivityFeed.tsx
 * PURPOSE: Grouped activity timeline with optimistic complete, shell search, loading skeletons
 * DEPENDENCIES: react, ../../context/CrmActionsContext, ../../context/CrmContext, ../../types/activity.types, ../../utils/crmShellSearchMatch, ./activityFeedBuckets, ./ActivityFeedEmptyPanel, ./ActivityFeedItem, ./ActivityFeedTypePills
 * PUBLIC_API: ActivityFeed, ActivityFeedProps
 * SIDE_EFFECTS: Optional onMarkComplete or `useCrmActions().markActivityComplete`
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 200/200
 * RECENT: 2026-04-30 [2.2.748] **`useCrmFilters`** from URL via **`crmUrlFilterProfileFromPathname`** — CHANGELOG [2.2.748]
 * RECENT_PRIOR: 2026-04-30 [2.2.742] **`accountId`** prop alias for account-scoped feed — CHANGELOG [2.2.742]
 */

import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCrmActions } from '../../context/CrmActionsContext';
import type { Activity } from '../../types/activity.types';
import { useCrmFilters } from '../../hooks/useCrmFilters';
import { crmUrlFilterProfileFromPathname } from '../../utils/crmFilterUrl';
import { rowMatchesShellSearch } from '../../utils/crmShellSearchMatch';
import { BUCKET_LABELS, BUCKET_ORDER, groupActivitiesByDateBucket } from './activityFeedBuckets';
import { ActivityFeedEmptyPanel } from './ActivityFeedEmptyPanel';
import { ActivityFeedItem } from './ActivityFeedItem';
import { ActivityFeedTypePills, type ActivityFeedTypeFilter } from './ActivityFeedTypePills';

export type ActivityFeedProps = {
  activities: Activity[];
  loading?: boolean;
  entityId?: string;
  /** Alias for `entityId` when the feed is scoped to a CRM contact row. */
  contactId?: string;
  /** Alias for `entityId` when the feed is scoped to a CRM account row. */
  accountId?: string;
  showLogButton?: boolean;
  onLogActivity?: () => void;
  maxItems?: number;
  /** When omitted, uses `useCrmActions().markActivityComplete` (requires `CrmActionsProvider`). */
  onMarkComplete?: (activityId: string) => Promise<void>;
};

function ActivityFeedSkeleton() {
  return (
    <ul className="space-y-6" aria-label="Loading activity skeleton">
      {Array.from({ length: 4 }, (_, i) => (
        <li key={i} className="flex animate-pulse gap-3">
          <div className="h-9 w-9 shrink-0 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function ActivityFeed({
  activities,
  loading = false,
  entityId,
  contactId,
  accountId,
  showLogButton = false,
  onLogActivity,
  maxItems,
  onMarkComplete,
}: ActivityFeedProps) {
  const scopedEntityId = entityId ?? contactId ?? accountId;
  const actions = useCrmActions();
  const { pathname } = useLocation();
  const { filters, setFilter } = useCrmFilters(crmUrlFilterProfileFromPathname(pathname));
  const [optimisticCompleteIds, setOptimisticCompleteIds] = useState<Set<string>>(new Set());
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ActivityFeedTypeFilter>('all');

  const markFn = useMemo(
    () => onMarkComplete ?? ((id: string) => actions.markActivityComplete(id)),
    [onMarkComplete, actions]
  );

  const filtered = useMemo(() => {
    let rows = activities.filter((a) =>
      rowMatchesShellSearch(filters.search, a.subject, a.ownerName, a.notes, a.outcome, a.type)
    );
    if (scopedEntityId) rows = rows.filter((a) => a.relatedEntityId === scopedEntityId);
    if (typeFilter !== 'all') rows = rows.filter((a) => a.type === typeFilter);
    if (typeof maxItems === 'number' && maxItems >= 0) rows = rows.slice(0, maxItems);
    return rows;
  }, [activities, scopedEntityId, filters.search, maxItems, typeFilter]);

  const displayActivity = useCallback(
    (a: Activity): Activity => {
      if (!optimisticCompleteIds.has(a.id)) return a;
      return { ...a, status: 'complete' };
    },
    [optimisticCompleteIds]
  );

  const handleMarkComplete = useCallback(
    async (a: Activity) => {
      if (a.status === 'complete') return;
      setOptimisticCompleteIds((prev) => new Set(prev).add(a.id));
      setCompletingId(a.id);
      try {
        await markFn(a.id);
      } catch {
        setOptimisticCompleteIds((prev) => {
          const next = new Set(prev);
          next.delete(a.id);
          return next;
        });
      } finally {
        setCompletingId(null);
      }
    },
    [markFn]
  );

  const shellClass =
    'rounded-xl border border-gray-200 bg-white/50 p-4 dark:border-gray-700 dark:bg-gray-800/50';

  if (loading) {
    return (
      <div
        data-testid="activity-feed"
        className={shellClass}
        aria-busy="true"
        aria-label="Loading activity"
      >
        <ActivityFeedTypePills value={typeFilter} onChange={setTypeFilter} />
        <ActivityFeedSkeleton />
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div data-testid="activity-feed" className={shellClass}>
        <ActivityFeedEmptyPanel
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          search={filters.search}
          scopedEntityId={scopedEntityId}
          onClearSearch={() => setFilter('search', '')}
          onLogActivity={onLogActivity}
        />
      </div>
    );
  }

  const buckets = groupActivitiesByDateBucket(filtered);

  return (
    <div data-testid="activity-feed" className={shellClass}>
      <ActivityFeedTypePills value={typeFilter} onChange={setTypeFilter} />
      {showLogButton && onLogActivity ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            data-testid="activity-feed-log-button"
            className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-primary-500 dark:hover:bg-primary-600"
            onClick={onLogActivity}
          >
            + Log activity
          </button>
        </div>
      ) : null}
      <div className="space-y-8" role="feed" aria-label="CRM activity timeline">
        {BUCKET_ORDER.map((bucket) => {
          const items = buckets[bucket];
          if (items.length === 0) return null;
          return (
            <section key={bucket} aria-labelledby={`activity-bucket-${bucket}`}>
              <h3
                id={`activity-bucket-${bucket}`}
                className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {BUCKET_LABELS[bucket]}
              </h3>
              <ul className="space-y-0">
                {items.map((raw, idx) => {
                  const a = displayActivity(raw);
                  return (
                    <ActivityFeedItem
                      key={a.id}
                      activity={a}
                      isLast={idx === items.length - 1}
                      completing={completingId === raw.id}
                      onMarkComplete={() => void handleMarkComplete(raw)}
                    />
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
